import { API_BASE_URL } from "@/lib/constants/app";
import { useFileStorageStore } from "@/store";
import { useCallback, useRef, useState } from "react";
import {
  ACCEPTED_FILE_TYPES,
  MAX_FILES,
  MAX_FILE_SIZE,
  REQUEST_EXPIRY,
} from "../constants/fileConstants";
import {
  detectNeedsAnalysis,
  isFileTypeSupported,
  isFileTypeSupportedEnhanced,
} from "../utils/fileUtils";
// SIMPLIFIED: Vector store management is now handled by thread store

// Request deduplication map
const processedRequests = new Map();

// Clear old requests periodically
setInterval(() => {
  const now = Date.now();
  for (const [key, timestamp] of processedRequests.entries()) {
    if (now - timestamp > REQUEST_EXPIRY) {
      processedRequests.delete(key);
    }
  }
}, 120000); // Clean up every 2 minutes

/**
 * Upload file and attach to vector store using simplified direct approach
 */
async function uploadAndAttachToVectorStore(file, vectorStoreId) {
  try {
    // Add file size check to prevent memory issues
    if (file.size > 50 * 1024 * 1024) {
      // 50MB limit
      throw new Error(
        `File too large: ${Math.round(file.size / 1024 / 1024)}MB. Maximum size is 50MB.`
      );
    }

    // Step 1: Convert file to base64 for backend JSON API

    const fileBuffer = await file.arrayBuffer();

    // Use FileReader for proper base64 conversion that's compatible with backend
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const uploadResponse = await fetch(`${API_BASE_URL}/api/ai/files/upload`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        file: base64,
        purpose: "assistants",
        filename: file.name,
      }),
    });

    if (!uploadResponse.ok) {
      const errorData = await uploadResponse.json().catch(() => ({}));
      throw new Error(
        `File upload failed: ${errorData.error || uploadResponse.status}`
      );
    }

    const uploadResult = await uploadResponse.json();
    const uploadedFile = uploadResult.file;

    // Step 2: Attach file to vector store using separate endpoint

    const attachResponse = await fetch(
      `${API_BASE_URL}/api/ai/vector-store/${vectorStoreId}/files/attach`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file_id: uploadedFile.id,
        }),
      }
    );

    if (!attachResponse.ok) {
      const errorData = await attachResponse.json().catch(() => ({}));
      throw new Error(
        `Failed to attach file to vector store: ${errorData.error || attachResponse.status}`
      );
    }

    const attachResult = await attachResponse.json();
    const vectorStoreFile = attachResult.uploadResponse;

    return {
      fileId: uploadedFile.id,
      vectorStoreFileId: vectorStoreFile.id,
    };
  } catch (error) {
    console.error("Error in upload and attach workflow:", error);
    throw error;
  }
}

export const useFileUpload = (currentThreadId, maxFiles = MAX_FILES, toast) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [storageFiles, setStorageFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const fileStorageStore = useFileStorageStore();

  // Handle removing a file from the files array
  const handleRemoveFile = useCallback((fileToRemove) => {
    // Clean up local URL if it exists
    if (fileToRemove.localUrl) {
      URL.revokeObjectURL(fileToRemove.localUrl);
    }

    setUploadedFiles((prevFiles) =>
      prevFiles.filter((file) => file.id !== fileToRemove.id)
    );
  }, []);

  // Handle clearing all files
  const handleClearFiles = useCallback(() => {
    setUploadedFiles((prevFiles) => {
      // Clean up all local URLs
      prevFiles.forEach((file) => {
        if (file.localUrl) {
          URL.revokeObjectURL(file.localUrl);
        }
      });
      return [];
    });
  }, []);

  // Handle adding more files
  const handleAddFiles = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle paste event for large text and images
  const handlePaste = useCallback(
    async (e) => {
      // Check for images first
      const clipboardItems = e.clipboardData.items;
      const imageItems = [];

      // Collect all image items from clipboard
      for (let i = 0; i < clipboardItems.length; i++) {
        const item = clipboardItems[i];
        if (item.type.startsWith("image/")) {
          imageItems.push(item);
        }
      }

      // If we have images, process them
      if (imageItems.length > 0) {
        e.preventDefault();

        if (uploadedFiles.length >= maxFiles) {
          toast({
            title: "Error",
            description: `Maximum ${maxFiles} files allowed`,
            variant: "destructive",
          });
          return;
        }

        // Process each image
        for (const imageItem of imageItems) {
          try {
            const blob = imageItem.getAsFile();
            if (!blob) continue;

            // Check file size
            if (blob.size > MAX_FILE_SIZE) {
              const maxSizeMB = MAX_FILE_SIZE / (1024 * 1024);
              toast({
                title: "Error",
                description: `Image size exceeds the limit of ${maxSizeMB} MB`,
                variant: "destructive",
              });
              continue;
            }

            // Create a proper filename with extension based on MIME type
            const getFileExtension = (mimeType) => {
              const extensions = {
                "image/png": "png",
                "image/jpeg": "jpg",
                "image/jpg": "jpg",
                "image/gif": "gif",
                "image/webp": "webp",
                "image/svg+xml": "svg",
                "image/bmp": "bmp",
                "image/tiff": "tiff",
              };
              return extensions[mimeType] || "png";
            };

            const extension = getFileExtension(blob.type);
            const filename = `pasted-image-${Date.now()}.${extension}`;

            // Create a File object from the blob
            const file = new File([blob], filename, { type: blob.type });

            // Create image preview file with local URL for immediate display
            const localUrl = URL.createObjectURL(file);
            const imagePreviewFile = {
              id: `paste-image-${Date.now()}`,
              name: file.name,
              size: file.size,
              type: file.type,
              isImage: true,
              isPdf: false,
              isDocument: false,
              status: "uploading",
              isLoading: true,
              isPasted: true,
              localUrl: localUrl, // Show image immediately
              file: file, // Keep reference to original file
            };

            // Add image preview file immediately
            setUploadedFiles((prev) => [...prev, imagePreviewFile]);
            setIsUploading(true);

            try {
              // SIMPLIFIED: Get or create vector store using thread store
              let threadVectorStoreId = null;
              let effectiveThreadId = currentThreadId;

              // If no currentThreadId, check for pending new chat thread
              if (!currentThreadId) {
                const { useThreadStore } = await import("@/store/threadStore");
                const threadStore = useThreadStore.getState();
                const pendingThreadId = threadStore.getPendingNewChatThread();

                if (pendingThreadId) {
                  effectiveThreadId = pendingThreadId;
                }
              }

              if (effectiveThreadId) {
                const { useThreadStore } = await import("@/store/threadStore");
                const threadStore = useThreadStore.getState();

                try {
                  threadVectorStoreId =
                    await threadStore.ensureThreadVectorStore(
                      effectiveThreadId
                    );
                } catch (vectorError) {
                  console.warn(
                    "Could not create vector store for thread:",
                    vectorError
                  );
                  // Continue without vector store - files can still be uploaded to storage
                }
              }

              // Create processed file object
              let processedFile = {
                id: imagePreviewFile.id,
                name: file.name,
                size: file.size,
                type: file.type,
                file,
                isImage: true,
                isPdf: false,
                isDocument: false,
                needsAnalysis: false,
                needsVision: true,
                needsFileSearch: false,
                status: "uploading",
                isPasted: true,
                localUrl: localUrl, // Keep local URL for immediate display
                vector_store_id: threadVectorStoreId,
              };

              // Update preview file to show uploading status
              setUploadedFiles((prev) =>
                prev.map((f) =>
                  f.id === imagePreviewFile.id
                    ? { ...f, status: "uploading" }
                    : f
                )
              );

              // Upload to Supabase chat bucket
              const supabaseUpload = await fileStorageStore.uploadChatFile(
                file,
                effectiveThreadId
              );

              if (supabaseUpload.error) {
                console.error("Supabase upload error:", supabaseUpload.error);
                throw new Error(
                  `Supabase upload failed: ${supabaseUpload.error.message}`
                );
              }

              // Update processed file with Supabase data
              processedFile = {
                ...processedFile,
                status: "uploaded",
                isLoading: false, // Explicitly set to false when upload is complete
                path: supabaseUpload.data?.path,
                publicUrl: supabaseUpload.data?.publicUrl,
                url: supabaseUpload.data?.publicUrl,
                storage_path: supabaseUpload.data?.path,
                bucketName: "chat",
                visionMetadata: {
                  publicUrl: supabaseUpload.data?.publicUrl,
                  contentType: file.type,
                  isReady: true,
                },
              };

              // Add to storage files for tracking
              setStorageFiles((prev) => [...prev, supabaseUpload]);

              // Upload to OpenAI and attach to vector store using proper workflow
              try {
                if (threadVectorStoreId && isFileTypeSupported(file.name)) {
                  const attachResult = await uploadAndAttachToVectorStore(
                    file,
                    threadVectorStoreId
                  );

                  processedFile.openai_file_id = attachResult.fileId;
                  processedFile.file_id = attachResult.fileId;
                  processedFile.vector_store_attached = true;
                  processedFile.vector_store_id = threadVectorStoreId;
                  processedFile.vector_store_file_id =
                    attachResult.vectorStoreFileId;
                  processedFile.vector_store_attached_at =
                    new Date().toISOString();
                  processedFile.embeddingsProcessed = true;
                  processedFile.embeddingProcessMethod = "vector_store";

                  // Update thread in database
                  try {
                    if (effectiveThreadId) {
                      const { supabase } = await import("@/lib/supabase");
                      await supabase
                        .from("threads")
                        .update({ vector_store_id: threadVectorStoreId })
                        .eq("id", effectiveThreadId);
                    }
                  } catch (dbError) {
                    console.error(
                      "Error updating thread with vector_store_id:",
                      dbError
                    );
                  }
                } else {
                  // For files that don't need vector store attachment, use regular upload
                  // Add file size check
                  if (file.size > 50 * 1024 * 1024) {
                    throw new Error(
                      `File too large: ${Math.round(file.size / 1024 / 1024)}MB. Maximum size is 50MB.`
                    );
                  }

                  // Use FileReader for proper base64 conversion
                  const base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                  });

                  const uploadResponse = await fetch(
                    `${API_BASE_URL}/api/ai/files/upload`,
                    {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        file: base64,
                        purpose: "assistants",
                        filename: file.name,
                      }),
                    }
                  );

                  let openaiFileUpload = null;
                  if (uploadResponse.ok) {
                    const uploadResult = await uploadResponse.json();
                    openaiFileUpload = uploadResult.file;
                  }

                  if (openaiFileUpload?.id) {
                    processedFile.openai_file_id = openaiFileUpload.id;
                    processedFile.file_id = openaiFileUpload.id;

                    // Mark for later vector store attachment if no thread ID yet
                    if (!effectiveThreadId && isFileTypeSupported(file.name)) {
                      processedFile.needsVectorStoreAttachment = true;
                      processedFile.pendingVectorStoreAttachment = true;
                    }
                  }
                }
              } catch (openaiError) {
                console.warn(
                  "OpenAI file upload failed but proceeding with Supabase URL:",
                  openaiError
                );
              }

              // Update storageFiles with complete metadata including OpenAI file ID
              setStorageFiles((prev) =>
                prev.map((sf) =>
                  sf.data?.path === supabaseUpload.data?.path
                    ? {
                        ...sf,
                        openai_file_id: processedFile.openai_file_id,
                        file_id: processedFile.file_id,
                        isImage: true,
                        needsVision: true,
                        name: file.name,
                        type: file.type,
                        size: file.size,
                        visionMetadata: processedFile.visionMetadata,
                        vector_store_attached:
                          processedFile.vector_store_attached,
                        vector_store_id: processedFile.vector_store_id,
                        vector_store_file_id:
                          processedFile.vector_store_file_id,
                        isPasted: true,
                      }
                    : sf
                )
              );

              // Replace preview file with processed file
              setUploadedFiles((prev) =>
                prev.map((f) =>
                  f.id === imagePreviewFile.id
                    ? { ...processedFile, isLoading: false }
                    : f
                )
              );

              // Clean up local URL to prevent memory leaks
              if (localUrl && localUrl !== processedFile.publicUrl) {
                setTimeout(() => URL.revokeObjectURL(localUrl), 1000);
              }
            } catch (error) {
              console.error("Failed to process pasted image:", error);

              // Remove preview file on error and clean up local URL
              setUploadedFiles((prev) =>
                prev.filter((f) => f.id !== imagePreviewFile.id)
              );
              URL.revokeObjectURL(localUrl);

              toast({
                title: "Error",
                description: `Failed to process pasted image: ${error.message}`,
                variant: "destructive",
              });
            } finally {
              setIsUploading(false);
            }
          } catch (error) {
            console.error("Error processing image item:", error);
            toast({
              title: "Error",
              description: "Failed to process pasted image",
              variant: "destructive",
            });
          }
        }
        return; // Early return after processing images
      }

      // If no images, check for large text content (existing functionality)
      const pastedText = e.clipboardData.getData("text");
      if (pastedText.length <= 10000) return; // Updated threshold to 10,000 characters

      e.preventDefault();

      if (uploadedFiles.length >= maxFiles) {
        toast({
          title: "Error",
          description: `Maximum ${maxFiles} files allowed`,
          variant: "destructive",
        });
        return;
      }

      // Create a blob and file object from the pasted text
      const blob = new Blob([pastedText], { type: "text/plain" });
      const file = new File([blob], `pasted-text-${Date.now()}.txt`, {
        type: "text/plain",
      });

      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: "Error",
          description: "Pasted content exceeds maximum file size",
          variant: "destructive",
        });
        return;
      }

      // Create skeleton file for immediate UI feedback
      const skeletonFile = {
        id: `paste-skeleton-${Date.now()}`,
        name: file.name,
        size: file.size,
        type: file.type,
        isImage: false,
        isPdf: false,
        isDocument: true,
        status: "loading",
        isLoading: true,
        isPasted: true,
      };

      // Add skeleton file immediately
      setUploadedFiles((prev) => [...prev, skeletonFile]);
      setIsUploading(true);

      try {
        // SIMPLIFIED: Get or create vector store using thread store
        const { useThreadStore } = await import("@/store/threadStore");
        const threadStore = useThreadStore.getState();
        const threadVectorStoreId =
          await threadStore.ensureThreadVectorStore(currentThreadId);

        if (!threadVectorStoreId) {
          console.error(
            "Failed to get or create vector store for thread:",
            currentThreadId
          );
          throw new Error("Vector store creation failed");
        }

        // Create processed file object
        let processedFile = {
          id: skeletonFile.id,
          name: file.name,
          size: file.size,
          type: file.type,
          file,
          isImage: false,
          isPdf: false,
          isDocument: true,
          needsAnalysis: true,
          needsVision: false,
          needsFileSearch: true,
          status: "uploading",
          isPasted: true,
          vector_store_id: threadVectorStoreId,
        };

        // Update skeleton to show uploading status
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === skeletonFile.id ? { ...f, status: "uploading" } : f
          )
        );

        // Upload to Supabase chat bucket
        const supabaseUpload = await fileStorageStore.uploadChatFile(
          file,
          currentThreadId
        );

        if (supabaseUpload.error) {
          console.error("Supabase upload error:", supabaseUpload.error);
          throw new Error(
            `Supabase upload failed: ${supabaseUpload.error.message}`
          );
        }

        // Update processed file with Supabase data
        processedFile = {
          ...processedFile,
          status: "uploaded",
          isLoading: false, // Explicitly set to false when upload is complete
          path: supabaseUpload.data?.path,
          publicUrl: supabaseUpload.data?.publicUrl,
          url: supabaseUpload.data?.publicUrl,
          storage_path: supabaseUpload.data?.path,
          bucketName: "chat",
        };

        // Add to storage files for tracking
        setStorageFiles((prev) => [...prev, supabaseUpload]);

        // Upload to OpenAI and attach to vector store using proper workflow
        try {
          if (threadVectorStoreId && isFileTypeSupported(file.name)) {
            const attachResult = await uploadAndAttachToVectorStore(
              file,
              threadVectorStoreId
            );

            processedFile.openai_file_id = attachResult.fileId;
            processedFile.file_id = attachResult.fileId;
            processedFile.vector_store_attached = true;
            processedFile.vector_store_id = threadVectorStoreId;
            processedFile.vector_store_file_id = attachResult.vectorStoreFileId;
            processedFile.vector_store_attached_at = new Date().toISOString();
            processedFile.embeddingsProcessed = true;
            processedFile.embeddingProcessMethod = "vector_store";

            // Update thread in database
            try {
              if (currentThreadId) {
                const { supabase } = await import("@/lib/supabase");
                await supabase
                  .from("threads")
                  .update({ vector_store_id: threadVectorStoreId })
                  .eq("id", currentThreadId);
              }
            } catch (dbError) {
              console.error(
                "Error updating thread with vector_store_id:",
                dbError
              );
            }
          } else {
            // For files that don't need vector store attachment, use regular upload

            // Add file size check
            if (file.size > 50 * 1024 * 1024) {
              throw new Error(
                `File too large: ${Math.round(file.size / 1024 / 1024)}MB. Maximum size is 50MB.`
              );
            }

            // Use FileReader for proper base64 conversion
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });

            const uploadResponse = await fetch(
              `${API_BASE_URL}/api/ai/files/upload`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  file: base64,
                  purpose: "assistants",
                  filename: file.name,
                }),
              }
            );

            let openaiFileUpload = null;
            if (uploadResponse.ok) {
              const uploadResult = await uploadResponse.json();
              openaiFileUpload = uploadResult.file;
            }

            if (openaiFileUpload?.id) {
              processedFile.openai_file_id = openaiFileUpload.id;
              processedFile.file_id = openaiFileUpload.id;
            }
          }
        } catch (openaiError) {
          console.warn("OpenAI file upload failed:", openaiError);
        }

        // Update storageFiles with complete metadata including OpenAI file ID
        setStorageFiles((prev) =>
          prev.map((sf) =>
            sf.data?.path === supabaseUpload.data?.path
              ? {
                  ...sf,
                  openai_file_id: processedFile.openai_file_id,
                  file_id: processedFile.file_id,
                  isImage: false,
                  isPdf: false,
                  isDocument: true,
                  needsAnalysis: true,
                  needsFileSearch: true,
                  isPasted: true,
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  vector_store_attached: processedFile.vector_store_attached,
                  vector_store_id: processedFile.vector_store_id,
                  vector_store_file_id: processedFile.vector_store_file_id,
                }
              : sf
          )
        );

        // Replace skeleton with processed file
        console.log(
          `🔄 Replacing skeleton with processed file for: ${processedFile.name}`,
          { status: processedFile.status, isLoading: false }
        );
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === skeletonFile.id
              ? { ...processedFile, isLoading: false }
              : f
          )
        );
      } catch (error) {
        console.error("Failed to process pasted content:", error);

        // Remove skeleton file on error
        setUploadedFiles((prev) =>
          prev.filter((f) => f.id !== skeletonFile.id)
        );

        toast({
          title: "Error",
          description: `Failed to process pasted content: ${error.message}`,
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
      }
    },
    [uploadedFiles.length, maxFiles, toast, currentThreadId, fileStorageStore]
  );

  // Handle file change event
  const handleFileChange = useCallback(
    async (e) => {
      // Check if this is a Google Drive file event
      if (e.isGoogleDrive && e.target?.googleDriveFiles) {
        await handleGoogleDriveFiles(e.target.googleDriveFiles);
        return;
      }

      // Get files from event object (local files)
      const fileList = e.target?.files;
      if (!fileList?.length) return;

      // Convert FileList to array
      const filesArray = Array.from(fileList);
      await processLocalFiles(filesArray);

      // Clear the file input value to allow selecting the same file again
      if (e.target && e.target.value) {
        e.target.value = "";
      }
    },
    [currentThreadId, uploadedFiles, toast]
  );

  // Process local files
  const processLocalFiles = useCallback(
    async (filesArray) => {
      // Generate a file upload request signature to prevent rapid duplicate uploads
      // Include current timestamp to allow same files to be uploaded again after some time
      const currentTime = Date.now();
      const filesSignature = filesArray
        .map((f) => `${f.name}-${f.size}-${f.lastModified}`)
        .sort()
        .join("|");
      const uploadRequestId = `upload-${filesSignature}-${currentTime}`;

      // Check if we've already processed this exact file upload very recently (within 1 second)
      // This prevents accidental rapid double-clicks but allows intentional re-uploads
      const recentRequests = Array.from(processedRequests.entries()).filter(
        ([key, timestamp]) => {
          // Extract signature from key: "upload-{signature}-{timestamp}"
          const keyParts = key.split("-");
          if (keyParts.length < 3) return false;

          // Remove "upload" prefix and timestamp suffix to get the signature
          const keySignature = keyParts.slice(1, -1).join("-");
          return (
            keySignature === filesSignature && currentTime - timestamp < 1000
          ); // 1 second
        }
      );

      if (recentRequests.length > 0) {
        console.log("Preventing rapid duplicate upload within 1 second");
        return;
      }

      // Mark this upload as processed
      processedRequests.set(uploadRequestId, currentTime);

      // Create a new array to track processed files
      let tempFiles = [];

      // Create skeleton loaders for all files being processed
      const skeletonFiles = filesArray.map((file) => {
        const isImage = file.type.startsWith("image/");
        return {
          id: `file-skeleton-${Date.now()}-${file.name}`,
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          isImage: isImage,
          isPdf: file.type === "application/pdf",
          isDocument:
            file.type.includes("document") ||
            file.type.includes("pdf") ||
            file.name.endsWith(".pdf") ||
            file.name.endsWith(".doc") ||
            file.name.endsWith(".docx"),
          status: isImage ? "uploading" : "loading", // Images show as uploading immediately
          isLoading: true,
          // For images, create local URL immediately for preview
          localUrl: isImage ? URL.createObjectURL(file) : undefined,
          file: isImage ? file : undefined, // Keep file reference for images
        };
      });

      // Immediately add skeleton files to the state BEFORE starting upload
      console.log(
        "🔄 Adding skeleton files:",
        skeletonFiles.map((f) => ({
          name: f.name,
          status: f.status,
          isLoading: f.isLoading,
        }))
      );
      setUploadedFiles((prev) => [...prev, ...skeletonFiles]);

      // Set uploading state after adding skeletons
      setIsUploading(true);
      console.log("🔄 Set global isUploading to true");

      // SIMPLIFIED: Get or create vector store using thread store
      // For new chats (no thread ID yet), check for pending thread or defer vector store creation
      let threadVectorStoreId = null;
      let effectiveThreadId = currentThreadId;

      // If no currentThreadId, check for pending new chat thread
      if (!currentThreadId) {
        const { useThreadStore } = await import("@/store/threadStore");
        const threadStore = useThreadStore.getState();
        const pendingThreadId = threadStore.getPendingNewChatThread();

        if (pendingThreadId) {
          effectiveThreadId = pendingThreadId;
        }
      }

      if (effectiveThreadId) {
        const { useThreadStore } = await import("@/store/threadStore");
        const threadStore = useThreadStore.getState();

        try {
          threadVectorStoreId =
            await threadStore.ensureThreadVectorStore(effectiveThreadId);
        } catch (vectorError) {
          console.warn(
            "Could not create vector store for thread:",
            vectorError
          );
          // Continue without vector store - files can still be uploaded to storage
        }
      }

      // Process each file
      for (const file of filesArray) {
        try {
          if (tempFiles.length + uploadedFiles.length >= MAX_FILES) {
            const message = `Maximum ${MAX_FILES} files allowed.`;
            toast({ title: message, status: "warning" });
            break;
          }

          if (file.size > MAX_FILE_SIZE) {
            const maxSizeMB = MAX_FILE_SIZE / (1024 * 1024);
            const message = `File size exceeds the limit of ${maxSizeMB} MB.`;
            toast({ title: message, status: "warning" });
            continue;
          }

          // Enhanced file type validation
          const isFileTypeAccepted = (() => {
            // First, explicitly reject xlsx files
            const fileExtension = file.name.split(".").pop()?.toLowerCase();
            if (
              fileExtension === "xlsx" ||
              file.type ===
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ) {
              return false;
            }

            // Check each MIME type pattern in ACCEPTED_FILE_TYPES
            for (const [mimeType, extensions] of Object.entries(
              ACCEPTED_FILE_TYPES
            )) {
              // Handle wildcard MIME types (e.g., "image/*", "text/*")
              if (mimeType.includes("*")) {
                const baseType = mimeType.split("/")[0];
                if (file.type.startsWith(baseType + "/")) {
                  // Also check if the file extension matches the expected extensions
                  const fileExtension = file.name
                    .split(".")
                    .pop()
                    ?.toLowerCase();
                  if (extensions.some((ext) => ext === fileExtension)) {
                    return true;
                  }
                }
              }
              // Handle exact MIME type matches
              else if (file.type === mimeType) {
                return true;
              }
              // Handle cases where browser MIME type might be slightly different
              // For example, some browsers report "text/json" instead of "application/json"
              else if (
                mimeType === "application/json" &&
                (file.type === "text/json" || file.type === "application/json")
              ) {
                return true;
              }
              // Also check by file extension as fallback
              else {
                const fileExtension = file.name.split(".").pop()?.toLowerCase();
                if (extensions.some((ext) => ext === fileExtension)) {
                  return true;
                }
              }
            }

            return false;
          })();

          if (!isFileTypeAccepted) {
            const message = `File type not accepted: ${file.type || "unknown"} (${file.name})`;
            toast({ title: message, status: "warning" });
            continue;
          }

          // Find the skeleton file that matches this file
          const skeletonId = skeletonFiles.find(
            (sf) => sf.name === file.name && sf.size === file.size
          )?.id;

          // Determine file type categories
          const isImage = file.type.startsWith("image/");
          const isPdf = file.type === "application/pdf";
          const isDocument =
            file.type.includes("document") ||
            file.type.includes("pdf") ||
            file.name.endsWith(".pdf") ||
            file.name.endsWith(".doc") ||
            file.name.endsWith(".docx");

          // Use detectNeedsAnalysis helper
          const needsAnalysis = detectNeedsAnalysis({
            type: file.type,
            name: file.name,
          });

          // Create the permanent ID that matches the skeleton ID or create a new one
          const permanentId = skeletonId || `file-${Date.now()}-${file.name}`;

          let processedFile = {
            id: permanentId,
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
            file,
            isImage,
            isPdf,
            isDocument,
            needsAnalysis,
            needsVision: isImage,
            needsFileSearch: isPdf || isDocument,
            status: "uploading",
            isLoading: false, // Explicitly set to false for processed files
            vector_store_id: threadVectorStoreId,
          };

          // Update skeleton to show uploading state
          console.log(`🔄 Updating skeleton to uploading for: ${file.name}`);
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.isLoading && f.name === file.name && f.size === file.size
                ? { ...f, status: "uploading", isLoading: true }
                : f
            )
          );

          // For images, upload to Supabase storage "chat" bucket
          if (isImage) {
            processedFile.status = "uploading";

            // Add local URL for immediate preview
            processedFile.localUrl = URL.createObjectURL(file);

            // Upload to Supabase chat bucket
            const supabaseUpload = await fileStorageStore.uploadChatFile(
              file,
              effectiveThreadId
            );

            if (supabaseUpload.error) {
              console.error("Supabase upload error:", supabaseUpload.error);
              toast({
                title: "Upload Error",
                description: `Failed to upload ${file.name}`,
                variant: "destructive",
              });
              processedFile.status = "failed";
            } else {
              // Update processed file with Supabase data
              processedFile = {
                ...processedFile,
                status: "uploaded",
                isLoading: false, // Explicitly set to false when upload is complete
                path: supabaseUpload.data?.path,
                publicUrl: supabaseUpload.data?.publicUrl,
                url: supabaseUpload.data?.publicUrl,
                visionMetadata: {
                  publicUrl: supabaseUpload.data?.publicUrl,
                  contentType: file.type,
                  isReady: true,
                },
              };

              // Clean up local URL once we have the Supabase URL
              if (
                processedFile.localUrl &&
                processedFile.localUrl !== processedFile.publicUrl
              ) {
                setTimeout(
                  () => URL.revokeObjectURL(processedFile.localUrl),
                  1000
                );
              }

              // Add to storageFiles for tracking
              setStorageFiles((prev) => [...prev, supabaseUpload]);

              // Upload to OpenAI and attach to vector store using proper workflow
              try {
                if (threadVectorStoreId && isFileTypeSupported(file.name)) {
                  const attachResult = await uploadAndAttachToVectorStore(
                    file,
                    threadVectorStoreId
                  );

                  processedFile.openai_file_id = attachResult.fileId;
                  processedFile.file_id = attachResult.fileId;
                  processedFile.vector_store_attached = true;
                  processedFile.vector_store_id = threadVectorStoreId;
                  processedFile.vector_store_file_id =
                    attachResult.vectorStoreFileId;
                  processedFile.vector_store_attached_at =
                    new Date().toISOString();
                  processedFile.embeddingsProcessed = true;
                  processedFile.embeddingProcessMethod = "vector_store";

                  // Update thread in database
                  try {
                    if (effectiveThreadId) {
                      const { supabase } = await import("@/lib/supabase");
                      await supabase
                        .from("threads")
                        .update({ vector_store_id: threadVectorStoreId })
                        .eq("id", effectiveThreadId);
                    }
                  } catch (dbError) {
                    console.error(
                      "Error updating thread with vector_store_id:",
                      dbError
                    );
                  }
                } else {
                  // Regular OpenAI upload for files that will be attached to vector store later
                  // or files that don't need vector store
                  // Add file size check
                  if (file.size > 50 * 1024 * 1024) {
                    throw new Error(
                      `File too large: ${Math.round(file.size / 1024 / 1024)}MB. Maximum size is 50MB.`
                    );
                  }

                  // Use FileReader for proper base64 conversion
                  const base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                  });

                  const uploadResponse = await fetch(
                    `${API_BASE_URL}/api/ai/files/upload`,
                    {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        file: base64,
                        purpose: "assistants",
                        filename: file.name,
                      }),
                    }
                  );

                  let openaiFileUpload = null;
                  if (uploadResponse.ok) {
                    const uploadResult = await uploadResponse.json();
                    openaiFileUpload = uploadResult.file;
                  }

                  if (openaiFileUpload?.id) {
                    processedFile.openai_file_id = openaiFileUpload.id;
                    processedFile.file_id = openaiFileUpload.id;

                    // Mark for later vector store attachment if no thread ID yet
                    if (!effectiveThreadId && isFileTypeSupported(file.name)) {
                      processedFile.needsVectorStoreAttachment = true;
                      processedFile.pendingVectorStoreAttachment = true;
                    }
                  }
                }
              } catch (openaiError) {
                console.warn(
                  "OpenAI file upload failed but proceeding with Supabase URL:",
                  openaiError
                );
              }

              // Update storageFiles with complete metadata including OpenAI file ID
              setStorageFiles((prev) =>
                prev.map((sf) =>
                  sf.data?.path === supabaseUpload.data?.path
                    ? {
                        ...sf,
                        openai_file_id: processedFile.openai_file_id,
                        file_id: processedFile.file_id,
                        isImage: true,
                        needsVision: true,
                        name: file.name,
                        type: file.type,
                        size: file.size,
                        visionMetadata: processedFile.visionMetadata,
                        vector_store_attached:
                          processedFile.vector_store_attached,
                        vector_store_id: processedFile.vector_store_id,
                        vector_store_file_id:
                          processedFile.vector_store_file_id,
                      }
                    : sf
                )
              );
            }
          } else {
            // For non-image files that need analysis, upload to both OpenAI and storage
            if (needsAnalysis || isPdf || isDocument) {
              processedFile.status = "uploading";

              // Upload to Supabase chat bucket first
              const supabaseUpload = await fileStorageStore.uploadChatFile(
                file,
                effectiveThreadId
              );

              if (supabaseUpload.error) {
                console.error("Supabase upload error:", supabaseUpload.error);
                processedFile.status = "failed";
              } else {
                // Update processed file with Supabase data
                processedFile = {
                  ...processedFile,
                  status: "uploaded",
                  isLoading: false, // Explicitly set to false when upload is complete
                  path: supabaseUpload.data?.path,
                  publicUrl: supabaseUpload.data?.publicUrl,
                  url: supabaseUpload.data?.publicUrl,
                  storage_path: supabaseUpload.data?.path,
                  bucketName: "chat",
                };

                // Add to storageFiles for tracking
                setStorageFiles((prev) => [...prev, supabaseUpload]);

                // Upload to OpenAI and attach to vector store using proper workflow
                try {
                  if (threadVectorStoreId && isFileTypeSupported(file.name)) {
                    const attachResult = await uploadAndAttachToVectorStore(
                      file,
                      threadVectorStoreId
                    );

                    processedFile.openai_file_id = attachResult.fileId;
                    processedFile.file_id = attachResult.fileId;
                    processedFile.vector_store_attached = true;
                    processedFile.vector_store_id = threadVectorStoreId;
                    processedFile.vector_store_file_id =
                      attachResult.vectorStoreFileId;
                    processedFile.vector_store_attached_at =
                      new Date().toISOString();
                    processedFile.embeddingsProcessed = true;
                    processedFile.embeddingProcessMethod = "vector_store";

                    // Update thread in database
                    try {
                      if (effectiveThreadId) {
                        const { supabase } = await import("@/lib/supabase");
                        await supabase
                          .from("threads")
                          .update({ vector_store_id: threadVectorStoreId })
                          .eq("id", effectiveThreadId);
                      }
                    } catch (dbError) {
                      console.error(
                        "Error updating thread with vector_store_id:",
                        dbError
                      );
                    }
                  } else {
                    // Regular OpenAI upload for files that will be attached to vector store later
                    // or files that don't need vector store
                    // Add file size check
                    if (file.size > 50 * 1024 * 1024) {
                      throw new Error(
                        `File too large: ${Math.round(file.size / 1024 / 1024)}MB. Maximum size is 50MB.`
                      );
                    }

                    // Use FileReader for proper base64 conversion
                    const base64 = await new Promise((resolve, reject) => {
                      const reader = new FileReader();
                      reader.onload = () => resolve(reader.result);
                      reader.onerror = reject;
                      reader.readAsDataURL(file);
                    });

                    const uploadResponse = await fetch(
                      `${API_BASE_URL}/api/ai/files/upload`,
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          file: base64,
                          purpose: "assistants",
                          filename: file.name,
                        }),
                      }
                    );

                    let openaiFileUpload = null;
                    if (uploadResponse.ok) {
                      const uploadResult = await uploadResponse.json();
                      openaiFileUpload = uploadResult.file;
                    }

                    if (openaiFileUpload?.id) {
                      processedFile.openai_file_id = openaiFileUpload.id;
                      processedFile.file_id = openaiFileUpload.id;

                      // Mark for later vector store attachment if no thread ID yet
                      if (
                        !effectiveThreadId &&
                        isFileTypeSupported(file.name)
                      ) {
                        processedFile.needsVectorStoreAttachment = true;
                        processedFile.pendingVectorStoreAttachment = true;
                      }
                    }
                  }
                } catch (openaiError) {
                  console.warn("OpenAI file upload failed:", openaiError);
                }

                // Update storageFiles with complete metadata including OpenAI file ID
                setStorageFiles((prev) =>
                  prev.map((sf) =>
                    sf.data?.path === supabaseUpload.data?.path
                      ? {
                          ...sf,
                          openai_file_id: processedFile.openai_file_id,
                          file_id: processedFile.file_id,
                          isImage: false,
                          isPdf: isPdf,
                          isDocument: isDocument,
                          needsAnalysis: needsAnalysis,
                          needsFileSearch: isPdf || isDocument,
                          name: file.name,
                          type: file.type,
                          size: file.size,
                          vector_store_attached:
                            processedFile.vector_store_attached,
                          vector_store_id: processedFile.vector_store_id,
                          vector_store_file_id:
                            processedFile.vector_store_file_id,
                        }
                      : sf
                  )
                );
              }
            } else {
              // Non-analysis files use original code
              processedFile.localUrl = URL.createObjectURL(file);
            }
          }

          // Add to temporary files
          tempFiles.push(processedFile);
        } catch (error) {
          console.error(`Error processing file: ${file.name}`, error);
          toast({
            title: `Failed to process file: ${file.name}`,
            status: "error",
          });

          // Remove the skeleton entry if there was an error
          setUploadedFiles((prev) =>
            prev.filter(
              (f) =>
                !(f.isLoading && f.name === file.name && f.size === file.size)
            )
          );
          continue;
        }
      }

      // Update files state
      console.log(
        "🔄 Final update - replacing skeletons with processed files:",
        tempFiles.map((f) => ({
          name: f.name,
          status: f.status,
          isLoading: f.isLoading,
        }))
      );
      setUploadedFiles((prev) => {
        // Get IDs of all newly processed files
        const processedFileIds = tempFiles.map((file) => file.id);

        // Clean up local URLs from skeleton files that are being replaced
        prev.forEach((file) => {
          if (
            file.isLoading &&
            processedFileIds.includes(file.id) &&
            file.localUrl
          ) {
            URL.revokeObjectURL(file.localUrl);
          }
        });

        // Filter out skeleton versions of processed files by ID
        const withoutSkeletons = prev.filter(
          (file) => !(file.isLoading && processedFileIds.includes(file.id))
        );

        const newState = [...withoutSkeletons, ...tempFiles];
        console.log(
          "🔄 New files state:",
          newState.map((f) => ({
            name: f.name,
            status: f.status,
            isLoading: f.isLoading,
          }))
        );
        return newState;
      });
      setIsUploading(false);
      console.log("🔄 Set global isUploading to false");

      // Clear the file input to allow selecting the same files again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [uploadedFiles, maxFiles, toast, currentThreadId, fileStorageStore]
  );

  // Handle Google Drive files
  const handleGoogleDriveFiles = useCallback(
    async (googleDriveFiles) => {
      if (!googleDriveFiles || googleDriveFiles.length === 0) return;

      // Get the access token for downloading files
      const accessToken =
        sessionStorage.getItem("google_drive_access_token") ||
        window.googleApiState?.accessToken;

      if (!accessToken) {
        console.error(
          "No Google Drive access token available for file download"
        );
        toast({
          title: "Authentication Error",
          description:
            "No access token available for downloading Google Drive files",
          variant: "destructive",
        });
        return;
      }

      // Create skeleton loaders for all Google Drive files being processed
      const skeletonFiles = googleDriveFiles.map((file) => ({
        id: `gdrive-skeleton-${Date.now()}-${file.id}`,
        name: file.name,
        size: file.sizeBytes || 0,
        type: file.mimeType,
        mimeType: file.mimeType,
        isGoogleDrive: true,
        isImage: file.mimeType?.startsWith("image/") || false,
        isPdf:
          file.mimeType === "application/pdf" ||
          file.name?.toLowerCase().endsWith(".pdf"),
        webViewLink: file.url,
        iconLink: file.iconUrl,
        status: "loading",
        isLoading: true,
      }));

      // Immediately add skeleton files to the state BEFORE starting processing
      setUploadedFiles((prev) => [...prev, ...skeletonFiles]);

      // Set uploading state after adding skeletons
      setIsUploading(true);

      // SIMPLIFIED: Get or create vector store using thread store
      // For new chats (no thread ID yet), we'll defer vector store creation until message send
      let threadVectorStoreId = null;

      if (currentThreadId) {
        const { useThreadStore } = await import("@/store/threadStore");
        const threadStore = useThreadStore.getState();

        try {
          threadVectorStoreId =
            await threadStore.ensureThreadVectorStore(currentThreadId);
        } catch (vectorError) {
          console.warn(
            "Could not create vector store for thread:",
            vectorError
          );
          // Continue without vector store - files can still be uploaded to storage
        }
      }

      // Track processed files for skeleton cleanup
      let processedFiles = [];

      for (const file of googleDriveFiles) {
        try {
          // Find the skeleton file that matches this Google Drive file
          const skeletonId = skeletonFiles.find(
            (sf) => sf.name === file.name && sf.size === (file.sizeBytes || 0)
          )?.id;

          // Update skeleton to show downloading state
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.isLoading &&
              f.name === file.name &&
              f.size === (file.sizeBytes || 0)
                ? { ...f, status: "downloading", isLoading: true }
                : f
            )
          );

          // Get the downloaded file content from the GoogleDriveButton
          const downloadedFile = file.fileObject;

          if (!downloadedFile) {
            console.error(
              `No file content available for Google Drive file: ${file.name}`
            );

            // Remove the skeleton entry if there was an error
            setUploadedFiles((prev) =>
              prev.filter(
                (f) =>
                  !(
                    f.isLoading &&
                    f.name === file.name &&
                    f.size === (file.sizeBytes || 0)
                  )
              )
            );
            continue;
          }

          // Update skeleton to show uploading state
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.isLoading &&
              f.name === file.name &&
              f.size === (file.sizeBytes || 0)
                ? { ...f, status: "uploading", isLoading: true }
                : f
            )
          );

          // Create the permanent ID that matches the skeleton ID or create a new one
          const permanentId = skeletonId || `gdrive-${Date.now()}-${file.id}`;

          const processedFile = {
            id: permanentId,
            name: downloadedFile.name,
            size: downloadedFile.size,
            type: downloadedFile.type,
            mimeType: downloadedFile.type,
            isGoogleDrive: true,
            originalGoogleMimeType:
              file.originalGoogleMimeType || file.mimeType,
            originalFileName: file.originalFileName || file.name,
            webViewLink: file.url,
            iconLink: file.iconUrl,
            file: downloadedFile, // The actual File object with content
            isFallbackFile: file.isFallbackFile || false,
            status: "uploading",
          };

          // Determine file type flags
          const isImage = downloadedFile.type?.startsWith("image/") || false;
          const isPdf =
            downloadedFile.type === "application/pdf" ||
            downloadedFile.name?.toLowerCase().endsWith(".pdf");

          processedFile.isImage = isImage;
          processedFile.isPdf = isPdf;

          // Initialize storage file metadata that will be used once at the end
          let storageFileMetadata = {
            isImage: isImage,
            isPdf: isPdf,
            isGoogleDrive: true,
            originalName: file.name,
            type: downloadedFile.type,
            name: downloadedFile.name,
            mimeType: downloadedFile.type,
            vector_store_attached: false,
          };

          // For images, handle vision metadata (only for actual images, not fallback files)
          if (isImage && !file.isFallbackFile) {
            // Upload to Supabase chat bucket for vision processing
            const supabaseUpload = await fileStorageStore.uploadChatFile(
              downloadedFile,
              currentThreadId
            );

            if (!supabaseUpload.error) {
              processedFile.path = supabaseUpload.data?.path;
              processedFile.publicUrl = supabaseUpload.data?.publicUrl;
              processedFile.url = supabaseUpload.data?.publicUrl;
              processedFile.visionMetadata = {
                isImage: true,
                publicUrl: supabaseUpload.data?.publicUrl,
                path: supabaseUpload.data?.path,
              };

              // Update storage metadata with Supabase info
              storageFileMetadata = {
                ...storageFileMetadata,
                ...supabaseUpload,
                data: {
                  path: supabaseUpload.data?.path,
                  publicUrl: supabaseUpload.data?.publicUrl,
                },
              };
            }
          }

          // Always attempt vector store attachment if we have a vector store ID and supported file
          if (
            threadVectorStoreId &&
            downloadedFile &&
            (isFileTypeSupportedEnhanced(
              downloadedFile.name,
              downloadedFile.type
            ) ||
              isFileTypeSupportedEnhanced(
                file.originalFileName || file.name,
                file.originalGoogleMimeType || file.mimeType
              ) ||
              file.isFallbackFile) // Always try to attach fallback files since they contain searchable metadata
          ) {
            try {
              const attachResult = await uploadAndAttachToVectorStore(
                downloadedFile,
                threadVectorStoreId
              );

              if (attachResult?.fileId) {
                processedFile.openai_file_id = attachResult.fileId;
                processedFile.file_id = attachResult.fileId;
                processedFile.vector_store_attached = true;
                processedFile.vector_store_file_id =
                  attachResult.vectorStoreFileId;

                // Update storage metadata with vector store info
                storageFileMetadata = {
                  ...storageFileMetadata,
                  openai_file_id: attachResult.fileId,
                  file_id: attachResult.fileId,
                  vector_store_file_id: attachResult.vectorStoreFileId,
                  vector_store_attached: true,
                };
              } else {
                console.error(
                  "Vector store attachment failed - no file ID returned"
                );
              }
            } catch (vectorError) {
              console.error(
                "Error attaching Google Drive file to vector store:",
                vectorError
              );

              // Still proceed with regular OpenAI upload as fallback
              try {
                // Add file size check
                if (downloadedFile.size > 50 * 1024 * 1024) {
                  throw new Error(
                    `File too large: ${Math.round(downloadedFile.size / 1024 / 1024)}MB. Maximum size is 50MB.`
                  );
                }

                // Use FileReader for proper base64 conversion
                const base64 = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result);
                  reader.onerror = reject;
                  reader.readAsDataURL(downloadedFile);
                });

                const uploadResponse = await fetch(
                  `${API_BASE_URL}/api/ai/files/upload`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                      file: base64,
                      purpose: "assistants",
                      filename: downloadedFile.name,
                    }),
                  }
                );

                let openaiFileUpload = null;
                if (uploadResponse.ok) {
                  const uploadResult = await uploadResponse.json();
                  openaiFileUpload = uploadResult.file;
                }

                if (openaiFileUpload?.id) {
                  processedFile.openai_file_id = openaiFileUpload.id;
                  processedFile.file_id = openaiFileUpload.id;

                  // Update storage metadata with OpenAI info
                  storageFileMetadata = {
                    ...storageFileMetadata,
                    openai_file_id: openaiFileUpload.id,
                    file_id: openaiFileUpload.id,
                    vector_store_attached: false,
                  };
                }
              } catch (uploadError) {
                console.error(
                  "Failed to upload Google Drive file to OpenAI:",
                  uploadError
                );
              }
            }
          } else {
            // For non-vector store files, still upload to OpenAI for general use
            try {
              // Add file size check
              if (downloadedFile.size > 50 * 1024 * 1024) {
                throw new Error(
                  `File too large: ${Math.round(downloadedFile.size / 1024 / 1024)}MB. Maximum size is 50MB.`
                );
              }

              // Use FileReader for proper base64 conversion
              const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(downloadedFile);
              });

              const uploadResponse = await fetch(
                `${API_BASE_URL}/api/ai/files/upload`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    file: base64,
                    purpose: "assistants",
                    filename: downloadedFile.name,
                  }),
                }
              );

              let openaiFileUpload = null;
              if (uploadResponse.ok) {
                const uploadResult = await uploadResponse.json();
                openaiFileUpload = uploadResult.file;
              }

              if (openaiFileUpload?.id) {
                processedFile.openai_file_id = openaiFileUpload.id;
                processedFile.file_id = openaiFileUpload.id;

                // Update storage metadata with OpenAI info
                storageFileMetadata = {
                  ...storageFileMetadata,
                  openai_file_id: openaiFileUpload.id,
                  file_id: openaiFileUpload.id,
                  vector_store_attached: false,
                };
              }
            } catch (uploadError) {
              console.error(
                "Failed to upload Google Drive file to OpenAI:",
                uploadError
              );
            }
          }

          // Mark as successfully uploaded
          processedFile.status = "uploaded";
          processedFile.isLoading = false; // Explicitly set to false when upload is complete

          // Add to storageFiles ONLY ONCE at the end with complete metadata
          setStorageFiles((prev) => [...prev, storageFileMetadata]);

          // Add to processed files array for skeleton cleanup
          processedFiles.push(processedFile);
        } catch (error) {
          console.error(
            `Error processing Google Drive file ${file.name}:`,
            error
          );

          toast({
            title: "Processing Error",
            description: `Failed to process "${file.name}": ${error.message}`,
            variant: "destructive",
          });

          // Remove the skeleton entry if there was an error
          setUploadedFiles((prev) =>
            prev.filter(
              (f) =>
                !(
                  f.isLoading &&
                  f.name === file.name &&
                  f.size === (file.sizeBytes || 0)
                )
            )
          );

          // Create a fallback entry for failed files
          const fallbackFile = {
            id: `gdrive-error-${Date.now()}-${file.id}`,
            name: file.name,
            size: file.sizeBytes || 0,
            type: file.mimeType,
            mimeType: file.mimeType,
            isGoogleDrive: true,
            webViewLink: file.url,
            iconLink: file.iconUrl,
            error: error.message,
            downloadFailed: true,
            status: "failed",
          };

          processedFiles.push(fallbackFile);
        }
      }

      // Update files state - clean up skeletons and add processed files
      setUploadedFiles((prev) => {
        // Get IDs of all newly processed files
        const processedFileIds = processedFiles.map((file) => file.id);

        // Filter out skeleton versions of processed files by ID
        const withoutSkeletons = prev.filter(
          (file) => !(file.isLoading && processedFileIds.includes(file.id))
        );

        const newState = [...withoutSkeletons, ...processedFiles];
        return newState;
      });

      // Clear uploading state
      setIsUploading(false);
    },
    [
      currentThreadId,
      uploadedFiles,
      toast,
      fileStorageStore,
      uploadAndAttachToVectorStore,
      setStorageFiles,
      setIsUploading,
      // ensureVectorStoreForThread removed - now handled by thread store
    ]
  );

  return {
    uploadedFiles,
    setUploadedFiles,
    storageFiles,
    setStorageFiles,
    isUploading,
    setIsUploading,
    fileInputRef,
    handleFileChange,
    handleGoogleDriveFiles,
    processLocalFiles,
    handleRemoveFile,
    handleClearFiles,
    handleAddFiles,
    handlePaste,
  };
};
